import { useState } from 'react';

interface QuotePopupProps {
  onClose: () => void;
}

export default function QuotePopup({ onClose }: QuotePopupProps) {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fullName = (form.querySelector('[name="input_text"]') as HTMLInputElement)?.value || '';
    const email = (form.querySelector('[name="email"]') as HTMLInputElement)?.value || '';
    const phone = (form.querySelector('[name="phone"]') as HTMLInputElement)?.value || '';
    const description = (form.querySelector('[name="description"]') as HTMLTextAreaElement)?.value || '';
    data.append('input_text', fullName);
    data.append('email', email);
    data.append('phone', phone);
    data.append('description', description);
    try {
      await fetch('https://readdy.ai/api/form/d7783cabj7ht1ofp4fqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md z-10 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100" style={{ backgroundColor: '#0a2342' }}>
          <div>
            <h2 className="text-lg font-black text-white">Quick Quote</h2>
            <p className="text-white/50 text-xs mt-0.5">We reply within 2 hours</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/15 hover:bg-white/25 cursor-pointer transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <i className="ri-close-line text-white text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 flex items-center justify-center rounded-full mx-auto mb-4" style={{ backgroundColor: '#d1fae5' }}>
                <i className="ri-checkbox-circle-fill text-3xl text-green-500" />
              </div>
              <h3 className="font-black text-gray-900 text-lg mb-1">Quote Request Sent!</h3>
              <p className="text-gray-500 text-sm mb-6">Our team will get back to you within 2 hours.</p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl text-sm font-black text-white cursor-pointer transition-all active:scale-95"
                style={{ backgroundColor: '#0a2342' }}
              >
                Close
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-3">
              <input
                name="input_text"
                type="text"
                placeholder="Full Name *"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors"
              />
              <input
                name="email"
                type="email"
                placeholder="Email Address *"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors"
              />
              <input
                name="phone"
                type="text"
                placeholder="Phone / WhatsApp (Optional)"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gray-400 transition-colors"
              />
              <textarea
                name="description"
                placeholder="What product do you need? (grade, size, quantity) *"
                required
                rows={3}
                maxLength={500}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-gray-400 resize-none transition-colors"
              />
              {status === 'error' && (
                <p className="text-red-500 text-xs">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full text-white py-3.5 rounded-xl text-sm font-black transition-all cursor-pointer disabled:opacity-60 active:scale-95 whitespace-nowrap"
                style={{ backgroundColor: '#0a2342' }}
              >
                {status === 'sending' ? (
                  <><i className="ri-loader-4-line animate-spin mr-2" />Sending...</>
                ) : (
                  <><i className="ri-send-plane-line mr-2" />Send Quote Request</>
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="w-full py-3 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-700 cursor-pointer transition-colors border border-gray-200 hover:border-gray-300"
              >
                Cancel
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
